import styled from '@emotion/styled'
import type { FC } from 'react'

import Divider from '@segment/matcha/components/Divider'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import Badge from '@segment/matcha/components/Badge'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import CardBase, * as Card from '@segment/matcha/recipes/Card'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import type { TitleWithCards as TitleWithCardsProps } from '../../data'

const TitleWithCards: FC<Omit<TitleWithCardsProps, `type`>> = ({ title, slug, cards, cta }) => {
  const pageConfig = usePageConfig()
  return (
    <Wrapper id={slug}>
      <CopyTextBase>
        <CopyText.Heading tag="h2" variant="h2">
          {title}
        </CopyText.Heading>
      </CopyTextBase>
      <StyledDivider />
      <ColumnTrack
        overrides={{
          md: 2,
        }}
      >
        {cards.map(({ title, description, category, href }, key) => (
          <CardBase href={getAbsoluteUrl(href, pageConfig)} key={key}>
            <Card.Body>
              {category && (
                <Card.Accessories>
                  <Card.AccessoriesRow>
                    <Badge variant="outlined">{category}</Badge>
                  </Card.AccessoriesRow>
                </Card.Accessories>
              )}
              <Card.Copy>
                <Card.Headline tag="h3">{title}</Card.Headline>
                <Card.Description>{description}</Card.Description>
              </Card.Copy>
              <Card.Action alignment="right">
                <ChevronRightIcon />
              </Card.Action>
            </Card.Body>
          </CardBase>
        ))}
      </ColumnTrack>
      {cta && (
        <MoreWrapper>
          <ButtonText href={getAbsoluteUrl(cta.href, pageConfig)} iconEnd={ChevronRightIcon}>
            {cta.title}
          </ButtonText>
        </MoreWrapper>
      )}
    </Wrapper>
  )
}

const StyledDivider = styled(Divider)`
  margin: 16px 0 40px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin: 16px 0 56px;
  }
`

const MoreWrapper = styled.div`
  margin-top: 40px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin-top: 56px;
  }
`

const Wrapper = styled.div`
  margin-top: 80px;
`

export default TitleWithCards
